import DOMPurify from 'dompurify';
import { ask } from 'src/store/backend';
import type { TargetLang } from './translation-types';

/**
 * On-demand content translation.
 *
 * The source language is always Norwegian (Bokmål). Translations are produced
 * by the existing `ask` endpoint (api.organization.ask), so this works without
 * the "ASK" feature flag being enabled for the organization.
 */

/** Target languages offered in the UI, in display order. */
export const TRANSLATION_LANGUAGES: TargetLang[] = ['en', 'sv', 'da', 'pl'];

/** Language shown first when the user asks for a translation. */
export const DEFAULT_TARGET_LANG: TargetLang = 'en';

/** English names used inside the LLM prompt. */
const LANGUAGE_ENGLISH_NAME: Record<TargetLang, string> = {
  en: 'English',
  sv: 'Swedish',
  da: 'Danish',
  pl: 'Polish',
};

/** Norwegian labels shown in the UI. */
export const LANGUAGE_LABEL: Record<TargetLang, string> = {
  en: 'Engelsk',
  sv: 'Svensk',
  da: 'Dansk',
  pl: 'Polsk',
};

/** Same allow-list as the HTML editor (d-edit-html), so links/structure survive. */
const SANITIZE_CONFIG = {
  ALLOWED_TAGS: ['h1', 'h2', 'h3', 'h6', 'ul', 'ol', 'li', 'i', 'em', 'b', 'p', 'a', 'br', 'strong'],
  ALLOWED_ATTR: ['href', 'target', 'checked', 'class'],
  ALLOW_DATA_ATTR: true,
};

/** Sanitize translated HTML before it is stored or rendered. */
export function sanitizeTranslatedHtml(html: string): string {
  return DOMPurify.sanitize(html, SANITIZE_CONFIG);
}

/**
 * Stable 32-bit FNV-1a hash of the source text. Stored alongside a translation
 * so we can detect when the Norwegian original has changed and the translation
 * has become stale.
 */
export function hashSource(...parts: string[]): string {
  const str = parts.join(' ');
  let hash = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    hash ^= str.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16);
}

/** Remove a leading/trailing markdown code fence the model may wrap output in. */
function stripCodeFence(text: string): string {
  return text
    .replace(/^\s*```[a-zA-Z]*\s*\n?/, '')
    .replace(/\n?```\s*$/, '')
    .trim();
}

function htmlPrompt(html: string, lang: TargetLang): string {
  const target = LANGUAGE_ENGLISH_NAME[lang];
  return `You are a professional translator for an occupational health & safety (HSE) system.
Translate the following HTML fragment from Norwegian (Bokmål) to ${target}.

Rules:
- Translate ONLY the human-readable text.
- Preserve every HTML tag, attribute and the structure exactly (h1-h3, h6, p, ul, ol, li, strong, b, em, i, a, br).
- NEVER change the value of href, class or any data-* attribute (for example data-internal-link-id, data-internal-link-doctype, data-internal-link-classified). Keep all internal links intact.
- Do NOT translate proper nouns: company, person, product, role or function names, or legal/standard references (for example "Arbeidstilsynet", "NS-EN").
- Use standard ${target} HSE terminology and keep it consistent.
- Return ONLY the translated HTML fragment, with no explanation and no markdown code fences.

HTML:
${html}`;
}

function textPrompt(text: string, lang: TargetLang): string {
  const target = LANGUAGE_ENGLISH_NAME[lang];
  return `Translate the following short title from Norwegian (Bokmål) to ${target}.
Do not translate proper nouns (company, person, product, role or function names).
Return ONLY the translated title as plain text, with no quotes and no explanation.

Title:
${text}`;
}

/** Machine-translate an HTML content fragment. Returns sanitized HTML. */
export async function translateHtmlContent(organizationId: string, html: string, lang: TargetLang): Promise<string> {
  if (!html || html.trim() === '') return '';
  const raw = await ask(organizationId, htmlPrompt(html, lang));
  return sanitizeTranslatedHtml(stripCodeFence(raw));
}

/** Machine-translate a short plain-text string such as a title. */
export async function translateText(organizationId: string, text: string, lang: TargetLang): Promise<string> {
  if (!text || text.trim() === '') return '';
  const raw = await ask(organizationId, textPrompt(text, lang));
  return stripCodeFence(raw);
}
